import type { PropsWithChildren, ReactNode } from "react";
import { Fragment } from "react";
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from "@headlessui/react";
import { tv } from "tailwind-variants";

import { icons } from "@/ui";
import { IconButton } from "./icon-button";

const modalVariants = tv({
  slots: {
    dialog:
      "m-4 w-full transform overflow-hidden rounded-card border border-line bg-surface text-left align-middle text-content shadow-overlay transition-all",
    wrapper: "relative",
    closeButton: "absolute top-3 right-3 z-40",
    icon: "size-5 fill-current",
  },
  variants: {
    size: {
      sm: { dialog: "max-w-md" },
      md: { dialog: "max-w-lg" },
      lg: { dialog: "max-w-2xl" },
    },
  },
  defaultVariants: {
    size: "lg",
  },
});

const { closeButton, dialog: dialogClass, icon, wrapper: wrapperClass } = modalVariants();

export type ModalSize = "sm" | "md" | "lg";

export type ModalProps = {
  isOpen?: boolean;
  size?: ModalSize;
  closeModal: () => void;
  title?: string;
  body?: ReactNode;
  className?: string;
  closeEnabled?: boolean;
};

export const Modal = ({
  body,
  children,
  className,
  closeEnabled = true,
  closeModal,
  isOpen,
  size,
  title,
}: PropsWithChildren<ModalProps>) => {
  const onClose = () => {
    if (closeEnabled) {
      closeModal();
    }
  };

  return (
    <Transition as={Fragment} show={isOpen} appear>
      <Dialog as="div" className="relative z-40" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm dark:bg-black/75" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center text-center">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className={dialogClass({ size })}>
                <div className={wrapperClass({ className })}>
                  <IconButton
                    className={closeButton({
                      className: closeEnabled ? "" : "cursor-default opacity-50",
                    })}
                    label="Close"
                    onClick={onClose}
                  >
                    <icons.XMarkIcon aria-hidden="true" className={icon()} />
                  </IconButton>

                  {(!!title || !!body) && (
                    <div className="flex flex-col gap-5 px-6 py-9 sm:px-10 sm:py-10 md:px-12">
                      {title ? (
                        <DialogTitle
                          as="h2"
                          className="text-2xl leading-snug font-bold text-balance text-content sm:text-3xl"
                        >
                          {title}
                        </DialogTitle>
                      ) : null}

                      {body ? <div className="leading-7 text-content-secondary">{body}</div> : null}
                    </div>
                  )}
                  {children}
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
